import React, { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { FlatList, Platform, Pressable, View, StyleSheet } from 'react-native';
import Text from '../../../components/Text';
import FeatherIcon from '@expo/vector-icons/Feather';
import EmptyList from '../../../components/EmptyList';
import LoadingSpinner from '../../../components/LoadingSpinner';
import NavbarWithAction from '../../../components/NavbarWithAction';
import RequireApproval from '../../../components/RequireApproval';
import {
  showErrorMessage,
  showInformationMessage,
  showSuccessMessage,
} from '../../../service/flashMessage';
import {
  createDateFrame,
  dateFrameRequestAccess,
  deleteUnitLinkDateFrame,
  fetchDateFrameByPerson,
  fetchDateFramesByUnit,
  updatePersonDateFrames,
} from '../../../api/dataframe';
import {
  convertDateFrameToDays,
  markCalendarDays,
} from '../../../utils/dateFrames';
import { ActivityIndicator, Card } from 'react-native-paper';
import { colorSwatches } from '../../../styles';
import { useFocusEffect } from '@react-navigation/native';
import CreateDateFramePanel from '../../../components/CreateDateFramePanel';
import { CalendarDateFrame, DateFrame } from '../../../@types/DateFrame';
import { isOneDayDateFrame, toLocalDateTime } from '../../../utils';
import { SYSTEM_FOREVER_DATEFRAME_ID } from '../../../constants';
import { confirmOneDayDateFrame, showAlert } from '../../../service';
import Button from '../../../components/Button';
import Ionicon from '@expo/vector-icons/Ionicons';
import HStack from '../../../components/HStack';
import Menu from '../../../components/Menu';
import OverflowMenu from '../../../components/OverflowMenu';
import Modal from '../../../components/Modal';
import Select from '../../../components/Select';
import VStack from '../../../components/VStack';
import Container from '../../../components/Container';
import FormContainer from '../../../components/FormContainer';
import AuthorizeButtons from '../components/AuthorizeButtons';
import { buildTodayAlertArgs, buildTommorowAlertArgs } from '../utils';
import { RequestAccess } from '../types';
import { store } from '../../../redux/store';
import { GetPeople } from '../../People/peopleReducer';
import Legend from '../components/Legend';
import CustomCalendar from '../components/CustomCalendar';

enum DATEFRAME_REQUEST_TYPE {
  TODAY = 'today',
  TOMMOROW = 'tommorow',
}

const UnitCalendar = ({ navigation, route }) => {
  const {
    unitId,
    unitPeopleLinkId,
    unitName,
    isLoggedInUser,
    personName,
    allowedToday,
    allowedTomorrow,
    navigateTo, // Add this
  } = route.params;
  const TITLE = `${isLoggedInUser ? 'Your' : personName
    } access for unit ${unitName}`;
  const [dateFrames, setDateFrames] = useState<Array<CalendarDateFrame>>([]);
  const [markedDates, setmarkedDates] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedDateFrame, setSelectedDateFrame] = useState('');
  const [panelActive, setPanelActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasForeverDateframe, setHasForeverDateFrame] = useState(true);
  const [editMode, setEditMode] = React.useState(false);
  const [editModeInitialState, setEditModeInitialState] = React.useState(null);
  const [loadingUnitDateFrames, setLoadingUnitDateFrames] = useState(false);
  const [unitDateFrames, setUnitDateFrames] = useState<Array<DateFrame> | []>(
    [],
  );
  const [loadingToday, setLoadingToday] = useState(false);
  const [loadingTommorow, setLoadingTommorow] = useState(false);
  const [allowedInToday, setAllowedInToday] = useState(allowedToday);
  const [allowedInTommorow, setAllowedInTommorow] = useState(allowedTomorrow);

  // add modalVisible to the RootStore to manage modal visibility globally
  useEffect(() => {
    store.dispatch({ type: 'UnitCalendar', unitCalendarModalVisible: modalVisible });
  }, [modalVisible]);
  useEffect(() => {
    store.dispatch({ type: 'UnitCalendar', unitCalendarCreateDateFrameVisible: panelActive });
  }, [panelActive]);

  useEffect(() => {
    fetchDateFrames(unitPeopleLinkId);
  }, [unitPeopleLinkId]);

  useFocusEffect(
    useCallback(() => {
      fetchDateFrames(unitPeopleLinkId);
    }, [unitPeopleLinkId]),
  );

  async function fetchDateFrames(linkId) {
    setIsLoading(true);
    try {
      let results: Array<CalendarDateFrame> = await fetchDateFrameByPerson(
        1,
        linkId,
      );
      const foreverDateFrame = results.filter(
        item => item.id === SYSTEM_FOREVER_DATEFRAME_ID,
      );
      setHasForeverDateFrame(foreverDateFrame.length > 0);
      const dateFramesInUnit = results.map(item => {
        return {
          ...item,
          days: convertDateFrameToDays(
            new Date(item.dateFrame.startDate),
            new Date(item.dateFrame.endDate),
          ),
        };
      });
      const marked = markCalendarDays(dateFramesInUnit);
      setmarkedDates(marked);
      setDateFrames(dateFramesInUnit);
      setIsLoading(false);
    } catch (error) {
      showErrorMessage('UnitCalendar: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  }

  const fetchUnitDateFrames = async (unitId: string) => {
    setLoadingUnitDateFrames(true);
    try {
      const res = await fetchDateFramesByUnit(1, unitId);
      setUnitDateFrames(res);
    } catch (error) {
      showErrorMessage('UnitCalendar - 2: ' + error.message);
    } finally {
      setLoadingUnitDateFrames(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const dateframeExists = dateFrames.find(
      item => item.dateFrame.id === selectedDateFrame,
    );
    if (dateframeExists) {
      showInformationMessage('Person already added to this dateframe.');
      setIsSubmitting(false);
      return;
    }
    const dateFrame = unitDateFrames.find(
      // @ts-ignore
      item => item?.id === selectedDateFrame,
    );

    if (!dateFrame) {
      showErrorMessage('Selected dateframe not found');
      setIsSubmitting(false);
      return;
    }

    const dateframeData = {
      unitId,
      startDate: toLocalDateTime(new Date(dateFrame.startDate), true),
      endDate: toLocalDateTime(new Date(dateFrame.endDate), false),
      description: dateFrame.shortDescription,
    };

    const shouldContinue = await confirmOneDayDateFrame(
      dateframeData.startDate,
      dateframeData.endDate,
    );

    if (!shouldContinue) {
      setIsSubmitting(false);
      return;
    }

    try {
      console.log('Submitting dateframe with data:', dateframeData);
      if (isLoggedInUser) {
        console.log('Calling dateFrameRequestAccess...');
        try {
          const accessResponse = await dateFrameRequestAccess(dateframeData);
          console.log('✓ dateFrameRequestAccess SUCCESS response:', accessResponse);
        } catch (apiError) {
          console.log('✗ dateFrameRequestAccess FAILED:', apiError);
          throw apiError;
        }
      } else {
        console.log('Calling createDateFrame...');
        try {
          const newDateFrame = await createDateFrame(dateframeData);
          console.log('✓ createDateFrame SUCCESS response:', newDateFrame);
          
          const dateFrameObj = {
            unitPeopleLinkId,
            description: dateFrame.shortDescription,
            dateFrameIds: [newDateFrame?.id],
            oneTimePass: isOneDayDateFrame(
              dateframeData.startDate,
              dateframeData.endDate,
            ),
          };
          console.log('Calling updatePersonDateFrames with:', dateFrameObj);
          try {
            const updateResponse = await updatePersonDateFrames(dateFrameObj);
            console.log('✓ updatePersonDateFrames SUCCESS response:', updateResponse);
          } catch (updateError) {
            console.log('✗ updatePersonDateFrames FAILED:', updateError);
            throw updateError;
          }
        } catch (createError) {
          console.log('✗ createDateFrame FAILED:', createError);
          throw createError;
        }
      }
      showSuccessMessage('Dateframe added successfully');
      setModalVisible(false);
      // navigation.goBack();
    } catch (error) {
      console.log('FINAL ERROR', error);
      console.log('error stack:', error.stack);
      showErrorMessage('UnitCalendar - 3: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePress = (unitlinkdateFrameId: string, onSuccess?: () => void) => {
    showAlert(
      'Confirm',
      'Are you sure you want to unlink this dateframe from user ?',
      () => console.log('NO'),
      async () => {
        try {
          await deleteUnitLinkDateFrame(unitlinkdateFrameId);
          showSuccessMessage('Dateframe unlinked from person');
          await fetchDateFrames(unitPeopleLinkId);
          //@ts-ignore
          store.dispatch(GetPeople());
          if (onSuccess) {
            onSuccess();
          }
        } catch (error) {
          showErrorMessage('UnitCalendar - 4: ' + error.message);
        }
      },
    );
  };

  const authorizeForToday = async () => {
    const { todayArguments, todayEndDate, todayStartDate, todayDescription } =
      buildTodayAlertArgs(personName, unitName);
    const request: RequestAccess = {
      unitId,
      startDate: todayStartDate,
      endDate: todayEndDate,
      description: todayDescription,
    };
    setLoadingToday(true);
    const shouldContinue = await confirmOneDayDateFrame(
      request.startDate,
      request.endDate,
    );

    if (!shouldContinue) {
      setLoadingToday(false);
      return;
    }

    await addDateFrameToPerson(request, DATEFRAME_REQUEST_TYPE.TODAY);
  };

  const addDateFrameToPerson = async (request: RequestAccess, type: DATEFRAME_REQUEST_TYPE) => {
    // If it's logged i user, they are requesting access, 
    // else it's probably an owner adding dateframe to a person
    try {
      if (isLoggedInUser) {
        await dateFrameRequestAccess({
          unitId: request.unitId,
          startDate: request.startDate,
          endDate: request.endDate,
          description: request.description,
        });
      }
      else {
        const newDateFrame = await createDateFrame({
          unitId: request.unitId,
          startDate: request.startDate,
          endDate: request.endDate,
          description: request.description,
        });
        const dateFrameObj = {
          unitPeopleLinkId,
          description: request.description,
          dateFrameIds: [newDateFrame?.id],
          oneTimePass: isOneDayDateFrame(request.startDate, request.endDate),
        };
        await updatePersonDateFrames(dateFrameObj);
      }
      showSuccessMessage('Request sent, you will be notified when accepted.');
      await fetchDateFrames(unitPeopleLinkId);
      //@ts-ignore
      store.dispatch(GetPeople());

      if (type === DATEFRAME_REQUEST_TYPE.TODAY) {
        setAllowedInToday(true);
      } else if (type === DATEFRAME_REQUEST_TYPE.TOMMOROW) {
        setAllowedInTommorow(true);
      }
    } catch (error) {
      if (type === DATEFRAME_REQUEST_TYPE.TODAY) {
        setAllowedInToday(false);
      } else if (type === DATEFRAME_REQUEST_TYPE.TOMMOROW) {
        setAllowedInTommorow(false);
      }
      showErrorMessage('UnitCalendar - 5: ' + error.message);
    } finally {
      setLoadingToday(false);
      setLoadingTommorow(false);

      if (type === DATEFRAME_REQUEST_TYPE.TODAY) {
        setLoadingToday(false);
      } else if (type === DATEFRAME_REQUEST_TYPE.TOMMOROW) {
        setLoadingTommorow(false);
      }
    }
  };

  const authorizeForTommorow = async () => {
    const {
      tommorowArguments,
      tommorowStartDate,
      tommorowEndDate,
      tommorowDescription,
    } = buildTommorowAlertArgs(personName, unitName);
    const request: RequestAccess = {
      unitId,
      startDate: tommorowStartDate,
      endDate: tommorowEndDate,
      description: tommorowDescription,
    };
    setLoadingTommorow(true);
    const shouldContinue = await confirmOneDayDateFrame(
      request.startDate,
      request.endDate,
    );

    if (!shouldContinue) {
      setLoadingTommorow(false);
      return;
    }

    await addDateFrameToPerson(request, DATEFRAME_REQUEST_TYPE.TOMMOROW);
  };

  const revokeForToday = async () => {
    // Find the dateframe that includes today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const todayDateFrame = dateFrames.find(item => {
      const startDate = new Date(item.dateFrame.startDate);
      const endDate = new Date(item.dateFrame.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      return startDate <= today && today <= endDate;
    });

    if (!todayDateFrame) {
      showErrorMessage('No dateframe found for today');
      return;
    }

    // Delete the dateframe using the existing delete handler
    handleDeletePress(todayDateFrame.id, () => setAllowedInToday(false));
  };

  const revokeForTommorow = async () => {
    // Find the dateframe that includes tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const tomorrowDateFrame = dateFrames.find(item => {
      const startDate = new Date(item.dateFrame.startDate);
      const endDate = new Date(item.dateFrame.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      return startDate <= tomorrow && tomorrow <= endDate;
    });

    if (!tomorrowDateFrame) {
      showErrorMessage('No dateframe found for tomorrow');
      return;
    }

    // Delete the dateframe using the existing delete handler
    handleDeletePress(tomorrowDateFrame.id, () => setAllowedInTommorow(false));
  };

  const renderHeader = () => {
    return (
      <>
        <AuthorizeButtons
          allowedToday={allowedInToday}
          allowedTommorow={allowedInTommorow}
          authorizeForToday={authorizeForToday}
          authorizeForTommorow={authorizeForTommorow}
          loadingToday={loadingToday}
          loadingTommorow={loadingTommorow}
          revokeForToday={revokeForToday}
          revokeForTommorow={revokeForTommorow}
        />
        <Card>
          <CustomCalendar markedDates={markedDates} />
        </Card>
        <Legend />
        <Text style={styles.headerText}>Dates List</Text>
      </>
    );
  };

  return (
    <Container hasDrawer fullHeight>
      <NavbarWithAction
        hasAdd={!hasForeverDateframe}
        hasBackArrow
        navigation={navigation}
        onClickAdd={() => {
          setEditMode(false);
          setModalVisible(true);
          fetchUnitDateFrames(unitId);
        }}
        hasSearch={false}
        title={TITLE}
        hasMoreMenu={false}
        hasProfile={false}
        hasSignOut={false}
        fullScreen
      />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <FlatList
          data={dateFrames}
          ListEmptyComponent={<EmptyList message="No dateframes found" />}
          renderItem={({ item }) => {
            const isForeverDateFrame =
              item.dateFrame.id === SYSTEM_FOREVER_DATEFRAME_ID;
            return (
              <DateFrameItem
                dateFrame={item}
                onClickApprovalsRequired={() => {
                  navigation.navigate('Approvals History', {
                    personName: 'Dateframe',
                    isLoggedInUser: false,
                    approvals: item.approvals,
                    type: 'pending',
                    recordName: item.dateFrame.shortDescription
                      ? item.dateFrame.shortDescription
                      : item.dateFrame.notes,
                  });
                }}
                handleDelete={() =>
                  !isForeverDateFrame && handleDeletePress(item.id)
                }
                handleEdit={() => {
                  if (!isForeverDateFrame) {
                    setEditModeInitialState(item);
                    setEditMode(true);
                    setPanelActive(true);
                  }
                }}
                isForeverDateFrame={isForeverDateFrame}
              />
            );
          }}
          ListHeaderComponent={renderHeader()}
          ListFooterComponent={
            <View style={{ height: Platform.OS === 'android' ? 60 : 120 }} />
          }
        />
      )}
      {modalVisible && (
        <View style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <View style={{
            backgroundColor: '#fff',
            borderRadius: 6,
            padding: 24,
            minWidth: 320,
            maxWidth: 400,
            minHeight: 260,
            width: '90%',
            elevation: 5,
          }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ fontWeight: 'bold', fontSize: 18 }}>Link dateframe to person</Text>
              <Pressable onPress={() => setModalVisible(false)}>
                <FeatherIcon name="x" size={24} color="gray" />
              </Pressable>
            </View>
            <FormContainer style={{ marginTop: 20, }}>
              <Text style={styles.modalText}>
                Select a Date frame or create a new one
              </Text>
              <HStack style={styles.hStack}>
                {loadingUnitDateFrames ? (
                  <HStack>
                    <ActivityIndicator size="small" />
                    <Text style={styles.loadingText}>
                      Loading dateframes...
                    </Text>
                  </HStack>
                ) : (
                  <Select
                    placeholder="Select dateframe"
                    py="1.5"
                    minWidth={200}
                    selectedValue={selectedDateFrame}
                    onValueChange={val => {
                      setSelectedDateFrame(val);
                    }}
                  >
                    {unitDateFrames.map((item: DateFrame) => (
                      <Select.Item
                        label={`${item.shortDescription}(${new Date(item.startDate).toLocaleDateString()} to ${new Date(item.endDate).toLocaleDateString()})`}
                        value={item.id}
                        key={item.id}
                      />
                    ))}
                  </Select>
                )}
                <Button
                  style={{ flex: 1, }}
                  ml={3}
                  py={1}
                  leftIcon={<FeatherIcon name="plus" size={20} />}
                  onPress={() => {
                    setModalVisible(false);
                    setPanelActive(true);
                  }}
                  variant="outline">
                  Create new
                </Button>
              </HStack>
              <Button
                isDisabled={isSubmitting || !selectedDateFrame}
                my="4"
                py="4"
                onPress={handleSubmit}>
                Submit
              </Button>
            </FormContainer>
          </View>
        </View>
      )}
      <CreateDateFramePanel
        editMode={editMode}
        initialState={editModeInitialState}
        unitId={unitId}
        unitPeopleLinkId={unitPeopleLinkId}
        navigation={navigation}
        panelActive={panelActive}
        setPanelActive={(state: boolean) => setPanelActive(state)}
        onSubmitSuccess={() => fetchDateFrames(unitPeopleLinkId)}
        isLoggedInUser={isLoggedInUser}
      />
    </Container>
  );
};

const DateFrameItem = ({
  dateFrame,
  onClickApprovalsRequired,
  handleDelete,
  handleEdit,
  isForeverDateFrame,
}) => {
  return (
    <Card style={styles.card}>
      <View style={styles.cardContent}>
        <View>
          <Text style={styles.dateFrameTitle}>
            {dateFrame.dateFrame.shortDescription
              ? dateFrame.dateFrame.shortDescription
              : dateFrame.dateFrame.notes}
          </Text>

          <Text style={styles.dateFrameDates}>
            {new Date(dateFrame.dateFrame.startDate).toLocaleDateString()} -{' '}
            {new Date(dateFrame.dateFrame.endDate).toLocaleDateString()}
          </Text>
          <RequireApproval
            approvals={dateFrame.approvals}
            onClickApprovalsRequired={() => onClickApprovalsRequired()}
          />
        </View>
        {!isForeverDateFrame && (
          <OverflowMenu
            trigger={triggerProps => {
              return (
                <Pressable {...triggerProps} style={styles.menuTrigger}>
                  <Ionicon name="ellipsis-vertical" size={22} />
                </Pressable>
              );
            }}>
            {/* <Menu.Item onPress={() => handleEdit()}>Edit</Menu.Item> */}
            <Menu.Item onPress={() => handleDelete()}>Delete</Menu.Item>
          </OverflowMenu>
        )}
      </View>
    </Card>
  );
};

const styles = StyleSheet.create({
  headerText: {
    marginTop: 16,
    padding: 8,
    fontSize: 16,
  },
  modalText: {
    // marginBottom: 8,
    marginTop: 8,
    // flex: 1,
  },
  hStack: {
    // flex: 1,
    marginTop: 12,
    marginBottom: 24
  },
  loadingText: {
    fontSize: 12,
    marginTop: 12,
    marginLeft: 12,
  },
  card: {
    borderRadius: 8,
    marginVertical: 8,
    padding: 8,
    marginHorizontal: 8,
  },
  cardContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  dateFrameTitle: {
    fontSize: 18,
  },
  dateFrameDates: {
    fontSize: 12,
  },
  menuTrigger: {
    paddingLeft: 16,
    paddingTop: 8,
  },
});

export default UnitCalendar;
